import MemberPromosDelegate from '../delegates/MemberPromos.delegate';
import TabController from './Tab.controller';
import Inherit from '../../../core/inherit';
import { SCOPES } from '../../../constants/scopes.constants';
import {
    DEFAULT_PROMO_DRAWER,
    DEFAULT_PROMO_DRAFT,
} from '../elements/feeStructureForm/elements/freeAdvances/freeAdvances.constants';
import { parseMemberIdCsv } from '../../../utils/csv';

/**
 * Members per bulk-enroll request. Must not exceed clockout-service's
 * Constants.CONFIG.FREE_ADVANCE_PROMO.MAX_MEMBERS_PER_BATCH (500) — the server
 * rejects a larger batch with 400 BATCH_TOO_LARGE rather than truncating it.
 */
const MEMBER_BATCH_SIZE = 500;

/**
 * Sub-controller for the Groups section inside the Free Advances sub-tab.
 *
 * Unlike VolumeTiersPanelController, this data does NOT live in
 * `fi_info`/`fee_rules` — groups and their members live in their own tables
 * behind their own REST endpoints (clockout-service sql/V11 + V12), gated
 * by the `feature:free_advance_promos:enabled` scope. So this controller
 * owns its own load/save lifecycle instead of deriving from `applyFromFi`,
 * and — deliberately — is never included in FeeStructureController's
 * snapshot/hasChanges/getValues: the drawer stages its own draft and
 * commits with its own Save, fully isolated from the tab's Save button.
 */
class MemberPromosController extends Inherit(TabController, MemberPromosDelegate) {
    constructor(parent) {
        super(parent.page || parent, 'memberPromos');
        this.parent = parent;
    }

    getInitialState() {
        return {
            groups: [],
            loadingGroups: false,
            // Whether the pilot feature scope is actually enabled for THIS
            // FI's instance. Starts false (not "unknown") so the section
            // never flashes visible-then-hidden — it only ever flips true
            // once loadGroups() gets a real, non-403 response. See
            // loadGroups() for why this can't be derived from hasScope()
            // alone.
            featureAvailable: false,
            drawer: { ...DEFAULT_PROMO_DRAWER },
        };
    }

    /**
     * The Configurations-manage scope — a real precondition (no point
     * calling the API for a role that can't write config at all), but NOT
     * sufficient on its own to know whether the pilot feature is live for
     * this FI. See `featureAvailable` / `loadGroups()`.
     */
    get canManage() {
        return this.page.hasScope(SCOPES.CONFIGURATIONS_MANAGE);
    }

    getAba() {
        return this.page.user?.fi?.aba || this.page.user?.access?.[0];
    }

    // --- Load -------------------------------------------------------------

    /**
     * Called by the orchestrator after initial state is set (same moment
     * as Segmentation#loadAccessLists). No-ops when CONFIGURATIONS_MANAGE
     * isn't held at all.
     *
     * The response is also the ONLY signal for whether the pilot feature
     * scope is enabled for this FI's instance: the backend route checks
     * hasScope(CONFIGURATIONS_MANAGE) AND isFreeAdvancePromosFeatureEnabled()
     * — a 403 here means the latter failed (the scope's `enabled` column is
     * off), since a 401/lacking-manage case would already have been caught
     * by the `canManage` guard above. There is no client-visible mirror of
     * `enabled` to check ahead of time — the Owner role holds the scope's
     * grant unconditionally the moment it exists in the catalog (V12's
     * auto-grant trigger), regardless of whether it's actually switched on,
     * so hasScope() alone would show this section in every FI, not just
     * the pilot's.
     */
    async loadGroups() {
        if (!this.canManage) return;
        const aba = this.getAba();
        if (!aba) return;

        this.setLoadingGroups(true);
        try {
            const groups = await this.page.data.fetchFreeAdvancePromos(aba);
            this.setState({ groups, loadingGroups: false, featureAvailable: true });
        } catch (error) {
            const status = error?.response?.status;
            if (status !== 403) {
                console.error('Error loading free-advance promo groups:', error);
            }
            // Fail closed: a 403 is a confirmed "feature disabled for this
            // FI", but any OTHER failure (500, network error, timeout,
            // expired session) is inconclusive — it says nothing about
            // whether the pilot is enabled. Treating it as "available" would
            // reveal this section, with an empty/broken roster, to FIs that
            // were never meant to see the pilot at all.
            this.setState({ loadingGroups: false, featureAvailable: false });
        }
    }

    // --- Drawer lifecycle ---------------------------------------------------

    /**
     * Opens the drawer. With no `promoId`, starts a blank create draft — a
     * new group has no id for members to attach to yet, so the whole thing
     * (name, rule, enabled, roster) is staged locally until Save. With a
     * `promoId`, seeds the draft from the already-loaded group summary (it
     * already carries label/count/window/enabled from the list endpoint —
     * no extra fetch needed) and kicks off the member roster load.
     */
    openGroupDrawer(promoId = null) {
        if (!promoId) {
            this.replaceDrawer({ ...DEFAULT_PROMO_DRAWER, open: true, mode: 'create' });
            return;
        }

        const group = this.state.groups.find((g) => g.id === promoId);
        this.replaceDrawer({
            ...DEFAULT_PROMO_DRAWER,
            open: true,
            mode: 'edit',
            promoId,
            loading: true,
            draft: group
                ? {
                      label: group.label,
                      free_advance_count: group.free_advance_count,
                      window_value: group.window_value,
                      window_unit: group.window_unit,
                      enabled: group.enabled,
                  }
                : { ...DEFAULT_PROMO_DRAFT },
        });
        this.loadDrawerMembers(promoId);
    }

    async loadDrawerMembers(promoId) {
        const aba = this.getAba();
        if (!aba) return;
        try {
            const members = await this.page.data.fetchFreeAdvancePromoMembers(aba, promoId, 'active');
            this.patchDrawer({ members: members || [], loading: false });
        } catch (error) {
            console.error('Error loading promo group members:', error);
            this.patchDrawer({ loading: false, error: 'Failed to load members' });
        }
    }

    closeGroupDrawer() {
        this.resetDrawer();
    }

    setDraftField(field, value) {
        this.setState((prev) => ({ drawer: { ...prev.drawer, draft: { ...prev.drawer.draft, [field]: value } } }));
    }

    /** Stages a member locally; the actual enroll POST happens on Save. */
    stageAddMember(memberId) {
        this.stageAddMembers([memberId]);
    }

    /**
     * Bulk-stages member IDs (single add and CSV import both land here) in
     * one setState. Skips ids already in the roster, and un-queues ids that
     * were pending removal — without that, "remove a loaded member, then
     * re-add them" would have Save run the enroll POST and THEN the queued
     * DELETE, silently removing a member the UI showed as added.
     *
     * @param {string[]} memberIds normalized ids (parseMemberIdCsv output
     *   or a single trimmed manual entry)
     * @returns {{added: number, duplicates: number}}
     */
    stageAddMembers(memberIds) {
        const existing = new Set(this.state.drawer.members.map((m) => m.member_id));

        const fresh = [];
        for (const memberId of memberIds) {
            if (existing.has(memberId)) continue;
            existing.add(memberId);
            fresh.push({ member_id: memberId, state: 'pending', _staged: true });
        }

        if (fresh.length) {
            const freshIds = new Set(fresh.map((m) => m.member_id));
            this.setState((prev) => ({
                drawer: {
                    ...prev.drawer,
                    members: [...prev.drawer.members, ...fresh],
                    removedMemberIds: prev.drawer.removedMemberIds.filter((id) => !freshIds.has(id)),
                },
            }));
        }

        return { added: fresh.length, duplicates: memberIds.length - fresh.length };
    }

    /**
     * CSV import, fully client-side: parse the file's text, stage every new
     * id, and report what happened. Staged members ride the exact same Save
     * flow as manually-added ones (one POST each, 409s downgraded to
     * warnings) — this exists purely so an admin can enroll hundreds of
     * member_ids in one gesture instead of typing them.
     */
    importMembersFromCsv(fileText) {
        const { ids, invalid } = parseMemberIdCsv(fileText);

        if (!ids.length) {
            this.page.props.notificationApi.warning({
                message: 'Nothing to import',
                description: invalid.length
                    ? `No valid member IDs found (${invalid.length} malformed ${invalid.length === 1 ? 'cell' : 'cells'}). Expected one member_id per line.`
                    : 'The file has no member IDs. Expected one member_id per line.',
            });
            return;
        }

        const { added, duplicates } = this.stageAddMembers(ids);

        const parts = [`${added} ${added === 1 ? 'member' : 'members'} staged`];
        if (duplicates) parts.push(`${duplicates} already in the list`);
        if (invalid.length)
            parts.push(`${invalid.length} malformed ${invalid.length === 1 ? 'cell' : 'cells'} skipped`);
        const description = `${parts.join(' · ')}. Save to enroll.`;

        if (added) {
            this.page.props.notificationApi.success({ message: 'CSV imported', description });
        } else {
            this.page.props.notificationApi.warning({ message: 'Nothing new to import', description });
        }
    }

    /**
     * Removes a member from the staged list. A member that was only just
     * staged (never saved) simply disappears; one that was already loaded
     * from the server is queued into `removedMemberIds` so Save issues the
     * DELETE for it.
     */
    stageRemoveMember(memberId) {
        this.setState((prev) => {
            const target = prev.drawer.members.find((m) => m.member_id === memberId);
            return {
                drawer: {
                    ...prev.drawer,
                    members: prev.drawer.members.filter((m) => m.member_id !== memberId),
                    removedMemberIds:
                        target && !target._staged
                            ? [...prev.drawer.removedMemberIds, memberId]
                            : prev.drawer.removedMemberIds,
                },
            };
        });
    }

    /**
     * Commits the drawer: create-or-update the group, then diff the staged
     * member list against what was loaded (add newly staged, remove queued
     * removals), then refresh the group list and close.
     *
     * Enrollment goes through the bulk endpoint in chunks of
     * MEMBER_BATCH_SIZE. It used to be one POST per member, which meant a
     * 1,000-member CSV import fired 1,000 serial requests and died on the
     * account's 60-per-minute rate limit at member #61 — with the group
     * already created and no rollback. A member the server rejects (already
     * active in another group) comes back in `conflicts` and is reported as a
     * warning; it never aborts the rest of the save.
     */
    async saveGroupDrawer() {
        if (!this.canManage) return;
        const aba = this.getAba();
        if (!aba) return;

        const { mode, promoId, draft, members, removedMemberIds } = this.state.drawer;
        this.patchDrawer({ saving: true, error: null });

        try {
            const savedGroup =
                mode === 'create'
                    ? await this.page.data.createFreeAdvancePromo(aba, draft)
                    : await this.page.data.updateFreeAdvancePromo(aba, promoId, draft);

            const targetPromoId = savedGroup.id;
            const stagedIds = members.filter((m) => m._staged).map((m) => m.member_id);
            const warnings = await this.enrollStagedMembers(aba, targetPromoId, stagedIds);
            await this.removeQueuedMembers(aba, targetPromoId, removedMemberIds);

            await this.loadGroups();
            this.closeGroupDrawer();

            if (warnings.length) {
                this.page.props.notificationApi.warning({
                    message: 'Group saved with warnings',
                    description: warnings.join('; '),
                });
            } else {
                this.page.props.notificationApi.success({
                    message: 'Success',
                    description: mode === 'create' ? 'Group created successfully' : 'Group updated successfully',
                });
            }
        } catch (error) {
            console.error('Error saving free-advance promo group:', error);
            const message = error?.response?.data?.error?.message || 'Failed to save group';
            this.patchDrawer({ saving: false, error: message });
        }
    }

    /**
     * Enrolls the staged member_ids into `promoId`, chunked to the server's
     * MAX_MEMBERS_PER_BATCH, and collects everything the admin should be told
     * about. Chunks run in series on purpose: they share one rate-limit
     * bucket, and the failure story for a half-applied import is far easier to
     * read when the chunks land in order.
     *
     * @returns {Promise<string[]>} Human-readable warnings — members enrolled
     *   into a non-active state (e.g. a typo'd id that isn't on the platform)
     *   and members rejected as already enrolled elsewhere.
     */
    async enrollStagedMembers(aba, promoId, memberIds) {
        const warnings = [];

        for (let i = 0; i < memberIds.length; i += MEMBER_BATCH_SIZE) {
            const chunk = memberIds.slice(i, i + MEMBER_BATCH_SIZE);
            const { members: saved, conflicts } = await this.page.data.addFreeAdvancePromoMembers(aba, promoId, chunk);

            for (const member of saved) {
                if (member?.state && member.state !== 'active') {
                    warnings.push(`${member.member_id}: ${member.state}`);
                }
            }

            for (const conflict of conflicts) {
                // Already active in THIS group is what the admin asked for —
                // the server only reports it so the count adds up.
                if (conflict.already_in_this_group) continue;
                warnings.push(
                    `${conflict.member_id}: already enrolled in ${conflict.current_promo_label || 'another group'}`,
                );
            }
        }

        return warnings;
    }

    /**
     * Removes the queued member_ids from `promoId`, chunked like the enroll
     * side. Produces no warnings on purpose: the endpoint is idempotent, so an
     * id with no active membership means the group already doesn't contain it
     * — exactly what was asked for. It used to be one DELETE per member, whose
     * 404 for that same harmless case had no handler and aborted the save.
     */
    async removeQueuedMembers(aba, promoId, memberIds) {
        for (let i = 0; i < memberIds.length; i += MEMBER_BATCH_SIZE) {
            const chunk = memberIds.slice(i, i + MEMBER_BATCH_SIZE);
            await this.page.data.removeFreeAdvancePromoMembers(aba, promoId, chunk);
        }
    }

    // --- Delete -------------------------------------------------------------

    async deleteGroup(promoId) {
        if (!this.canManage) return;
        const aba = this.getAba();
        if (!aba) return;

        try {
            await this.page.data.deleteFreeAdvancePromo(aba, promoId);
            this.removeGroup(promoId);
            this.page.props.notificationApi.success({
                message: 'Success',
                description: 'Group deleted successfully',
            });
        } catch (error) {
            console.error('Error deleting free-advance promo group:', error);
            this.page.props.notificationApi.error({
                message: 'Error',
                description: 'Failed to delete group',
            });
        }
    }

    // --- View props -----------------------------------------------------

    getViewProps() {
        const { groups, loadingGroups, drawer, featureAvailable } = this.state;

        return {
            // Both required: canManage alone would show this section (with
            // an empty, permanently-403ing roster) in every non-pilot FI —
            // see featureAvailable's own comment in getInitialState().
            visible: this.canManage && featureAvailable,
            canManage: this.canManage,
            groups,
            loadingGroups,
            drawer,
            onAddGroup: () => this.openGroupDrawer(),
            onOpenMembers: (promoId) => this.openGroupDrawer(promoId),
            onEditGroup: (promoId) => this.openGroupDrawer(promoId),
            onDeleteGroup: (promoId) => this.deleteGroup(promoId),
            onCloseDrawer: () => this.closeGroupDrawer(),
            onDrawerFieldChange: (field, value) => this.setDraftField(field, value),
            onStageAddMember: (memberId) => this.stageAddMember(memberId),
            onImportMembersCsv: (fileText) => this.importMembersFromCsv(fileText),
            onStageRemoveMember: (memberId) => this.stageRemoveMember(memberId),
            onSaveDrawer: () => this.saveGroupDrawer(),
        };
    }
}

export default MemberPromosController;
